import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import ConfirmationDialog from '@/components/ui/ConfirmationDialog/ConfirmationDialog';
import { usePostAuthApi } from '@/hooks/UseApiCalls';
import NedarimStandingOrderDtoIn from '@/models/NedarimStandingOrderDtoIn';
import { formatDate, formatRowMoney, parseDate } from '../standingOrderModel';
import { StandingOrderActionResult, actionUrl, describeActionError } from '../standingOrderActions';
import { isAptOrder } from '../aptStandingOrders';
import StopReasonField from './StopReasonField';

interface ToggleOrderDialogProps {
  kind: 'freeze' | 'reactivate';
  order: NedarimStandingOrderDtoIn;
  fundId: number;
  onClose: () => void;
  onDone: (kind: 'freeze' | 'reactivate', result: StandingOrderActionResult) => void;
}

/**
 * Freezing and reactivating: one question each, in the site's own confirmation dialog. A freeze
 * is the action the board offers first because nothing is lost by it - the card and the details
 * stay with Nedarim, and the same button brings the order back.
 *
 * When Nedarim refuse, the dialog stays open and says why in their words. A reactivation most
 * often fails on an order with no card saved, and the way out of that is already in the blade.
 */
const ToggleOrderDialog = ({ kind, order, fundId, onClose, onDone }: ToggleOrderDialogProps) => {
  const { t, i18n } = useTranslation();
  const [error, setError] = useState<string | null>(null);
  const prefix = `standingOrders.orderActions.${kind}Dialog`;
  // A bank order in APT (HAT-1441): no card, and the office's file to the bank decides when it takes effect.
  const isApt = isAptOrder(order);
  const bankPrefix = `standingOrders.orderActions.bank.${kind}Dialog`;
  // Why the bank order is frozen, into APT's «מצב הוראה» (the office, 21.10.2026).
  const askReason = isApt && kind === 'freeze';
  const [reason, setReason] = useState('');

  const { mutate, isPending } = usePostAuthApi<StandingOrderActionResult, object>(actionUrl(fundId, order, kind), {
    onSuccess: (result) => {
      if (result.success) {
        onDone(kind, result);
        return;
      }
      const refused = t('standingOrders.orderActions.errors.nedarimRefused', { message: result.message ?? '' });
      setError(kind === 'reactivate' ? `${refused}\n${t(`${prefix}.cardHint`)}` : refused);
    },
    onError: (e) => setError(describeActionError(e, t, isApt)),
  });

  const name = order.clientName || t('standingOrders.donor');
  const next = parseDate(order.nextDate);
  const amount = formatRowMoney(order.amount, order, i18n.language);

  /*
   * The credit wording, word for word, wherever it holds for a bank order (Chaya, 19.10.2026). A
   * freeze names the mandate where a credit freeze names the card; restarting a cancelled order says
   * so rather than "the freeze". No "the charge may already be with the bank": the office locks an
   * order's collection before it sends the file, and the board offers no action on a locked order.
   */
  const message = (() => {
    if (isApt && kind === 'freeze') {
      return next
        ? t(`${bankPrefix}.message`, { date: formatDate(next, i18n.language) })
        : t(`${bankPrefix}.messageNoDate`);
    }
    if (isApt && order.aptCancelledFromSite) {
      return t(`${bankPrefix}.messageCancelled`, { amount });
    }
    return kind === 'freeze'
      ? (next
        ? t(`${prefix}.message`, { date: formatDate(next, i18n.language) })
        : t(`${prefix}.messageNoDate`))
      : t(`${prefix}.message`, { amount });
  })();

  return (
    <ConfirmationDialog
      open
      onOpenChange={(open) => { if (!open && !isPending) onClose(); }}
      title={t(`${prefix}.title`, { name })}
      message={message}
      confirmText={`${prefix}.confirm`}
      cancelText={`${prefix}.cancel`}
      onConfirm={() => { setError(null); mutate(askReason && reason.trim() ? { reason: reason.trim() } : {}); }}
      isLoading={isPending}
      withIcon
      error={error}
    >
      {askReason && (
        <StopReasonField id="freeze-standing-order-reason" value={reason} onChange={setReason} disabled={isPending} />
      )}
    </ConfirmationDialog>
  );
};

export default ToggleOrderDialog;
